import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  MapPin, 
  Calendar,
  ArrowLeft,
  Navigation,
  Map,
  Zap,
  ZapOff
} from 'lucide-react';

// Leaflet imports
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatUtcTime } from '@/utils/dateUtils';

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const createIcon = (color) => new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="
    width: 20px;
    height: 20px;
    background-color: ${color};
    border: 3px solid white;
    border-radius: 50%;
    box-shadow: 0 2px 5px rgba(0,0,0,0.3);
  "></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  popupAnchor: [0, -10],
});

const startIcon = createIcon('#10B981'); // Green
const endIcon = createIcon('#EF4444');   // Red
const activeIcon = createIcon('#6366F1'); // Indigo
const idleIcon = createIcon('#F59E0B');   // Amber

// Component to fit map bounds
function FitBounds({ positions }) {
  const map = useMap();
  
  useEffect(() => {
    if (positions.length > 0) {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [positions, map]);
  
  return null;
}

function StaffGPSTrackingPage() {
  const { t, isRTL } = useLanguage();
  const { staffId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [gpsRecords, setGpsRecords] = useState([]);
  const [stats, setStats] = useState({ total_kilometers: 0, total_records: 0, idle_count: 0, active_count: 0 });
  const [staffName, setStaffName] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(searchParams.get('date') || new Date().toISOString().split('T')[0]);
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    fetchGPSData();
  }, [staffId, selectedDate]);

  const fetchGPSData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/activities/gps/${staffId}`, {
        params: { date: selectedDate },
        withCredentials: true
      });
      setGpsRecords(response.data.gps_records || []);
      setStats(response.data.stats || { total_kilometers: 0, total_records: 0, idle_count: 0, active_count: 0 });
      setStaffName(response.data.staff_name || '');
    } catch (error) {
      console.error('Failed to fetch GPS data:', error);
    } finally {
      setLoading(false);
    }
  };

    const formatTime = (value) => formatUtcTime(value);


  // Prepare map data
  const positions = gpsRecords.map(record => [record.latitude, record.longitude]);
  const centerPosition = positions.length > 0 
    ? [
        positions.reduce((sum, pos) => sum + pos[0], 0) / positions.length,
        positions.reduce((sum, pos) => sum + pos[1], 0) / positions.length
      ]
    : [37.7749, -122.4194]; // Default to San Francisco

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/admin/activities')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <MapPin className="w-6 h-6 text-indigo-600" />
              GPS Tracking - {staffName}
            </h2>
            <p className="text-gray-600">View location history and travel routes</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-500" />
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-40"
              data-testid="date-picker"
            />
          </div>
          {gpsRecords.length > 0 && (
            <Button 
              onClick={() => setShowMap(true)}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="show-map-btn"
            >
              <Map className="w-4 h-4 mr-2" />
              Show on Map
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-indigo-500 to-purple-500 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-sm">Total Distance</p>
                <p className="text-3xl font-bold">{stats.total_kilometers.toFixed(2)} km</p>
              </div>
              <Navigation className="w-10 h-10 text-indigo-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-indigo-500 to-purple-500 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-sm">Total Records</p>
                <p className="text-3xl font-bold">{stats.total_records}</p>
              </div>
              <MapPin className="w-10 h-10 text-indigo-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-indigo-500 to-indigo-500 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-sm">Active (Moving)</p>
                <p className="text-3xl font-bold">{stats.active_count}</p>
              </div>
              <Zap className="w-10 h-10 text-indigo-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Idle (Stationary)</p>
                <p className="text-3xl font-bold">{stats.idle_count}</p>
              </div>
              <ZapOff className="w-10 h-10 text-amber-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* GPS Data Table */}
      <Card>
        <CardHeader className="border-b">
          <CardTitle>GPS Location History</CardTitle>
          <CardDescription>Detailed location records for the selected date</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : gpsRecords.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <MapPin className="w-16 h-16 mb-4 opacity-50" />
              <p className="text-lg">No GPS records for this date</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">#</TableHead>
                    <TableHead className="font-semibold">Time</TableHead>
                    <TableHead className="font-semibold">Latitude</TableHead>
                    <TableHead className="font-semibold">Longitude</TableHead>
                    <TableHead className="font-semibold">Accuracy (m)</TableHead>
                    <TableHead className="font-semibold">Distance (km)</TableHead>
                    <TableHead className="font-semibold">Speed (m/s)</TableHead>
                    <TableHead className="font-semibold">Event</TableHead>
                    <TableHead className="font-semibold">{t("common.status")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {gpsRecords.map((record, index) => (
                    <TableRow key={record.gps_id} className="hover:bg-gray-50">
                      <TableCell className="font-medium">{index + 1}</TableCell>
                      <TableCell>{formatTime(record.timestamp)}</TableCell>
                      <TableCell className="font-mono text-sm">{record.latitude.toFixed(6)}</TableCell>
                      <TableCell className="font-mono text-sm">{record.longitude.toFixed(6)}</TableCell>
                      <TableCell>{record.accuracy?.toFixed(1) || '-'}</TableCell>
                      <TableCell>{record.distance_km?.toFixed(3) || '0'}</TableCell>
                      <TableCell>{record.speed?.toFixed(2) || '0'}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          record.event_type === 'check_in' ? 'bg-indigo-100 text-indigo-800' :
                          record.event_type === 'check_out' ? 'bg-red-100 text-red-800' :
                          record.event_type === 'break_start' ? 'bg-amber-100 text-amber-800' :
                          record.event_type === 'break_end' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {record.event_type || 'update'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          record.movement_status === 'idle' 
                            ? 'bg-amber-100 text-amber-800' 
                            : 'bg-indigo-100 text-indigo-800'
                        }`}>
                          {record.movement_status === 'idle' ? 'Idle' : 'Active'}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Map Modal with Leaflet */}
      <Dialog open={showMap} onOpenChange={setShowMap}>
        <DialogContent className="max-w-5xl h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Map className="w-5 h-5 text-indigo-600" />
              Travel Route - {staffName}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 relative h-[calc(80vh-100px)]">
            {gpsRecords.length > 0 && (
              <MapContainer
                center={centerPosition}
                zoom={14}
                className="w-full h-full rounded-lg"
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                
                {/* Route polyline */}
                <Polyline 
                  positions={positions}
                  pathOptions={{ 
                    color: '#6366F1', 
                    weight: 4, 
                    opacity: 0.8,
                    dashArray: null
                  }}
                />
                
                {/* Start marker */}
                {gpsRecords.length > 0 && (
                  <Marker 
                    position={[gpsRecords[0].latitude, gpsRecords[0].longitude]}
                    icon={startIcon}
                  >
                    <Popup>
                      <div className="text-sm">
                        <strong className="text-indigo-600">Start Point</strong>
                        <br />
                        Time: {formatTime(gpsRecords[0].timestamp)}
                        <br />
                        Event: {gpsRecords[0].event_type}
                      </div>
                    </Popup>
                  </Marker>
                )}
                
                {/* End marker */}
                {gpsRecords.length > 1 && (
                  <Marker 
                    position={[gpsRecords[gpsRecords.length - 1].latitude, gpsRecords[gpsRecords.length - 1].longitude]}
                    icon={endIcon}
                  >
                    <Popup>
                      <div className="text-sm">
                        <strong className="text-red-600">End Point</strong>
                        <br />
                        Time: {formatTime(gpsRecords[gpsRecords.length - 1].timestamp)}
                        <br />
                        Event: {gpsRecords[gpsRecords.length - 1].event_type}
                      </div>
                    </Popup>
                  </Marker>
                )}
                
                {/* Intermediate markers */}
                {gpsRecords.slice(1, -1).map((record, index) => (
                  <Marker
                    key={record.gps_id}
                    position={[record.latitude, record.longitude]}
                    icon={record.movement_status === 'idle' ? idleIcon : activeIcon}
                  >
                    <Popup>
                      <div className="text-sm">
                        <strong>Point {index + 2}</strong>
                        <br />
                        Time: {formatTime(record.timestamp)}
                        <br />
                        Status: {record.movement_status}
                        <br />
                        Distance: {record.distance_km?.toFixed(3) || 0} km
                      </div>
                    </Popup>
                  </Marker>
                ))}
                
                {/* Fit bounds to show all markers */}
                <FitBounds positions={positions} />
              </MapContainer>
            )}
            
            {/* Legend */}
            <div className="absolute bottom-4 left-4 bg-white p-3 rounded-lg shadow-lg z-[1000]">
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-indigo-500 border-2 border-white shadow"></div>
                  <span>Start</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500 border-2 border-white shadow"></div>
                  <span>End</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-indigo-500 border-2 border-white shadow"></div>
                  <span>{t("common.active")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-500 border-2 border-white shadow"></div>
                  <span>Idle</span>
                </div>
              </div>
            </div>
            
            {/* Stats overlay */}
            <div className="absolute top-4 right-4 bg-white p-3 rounded-lg shadow-lg z-[1000]">
              <div className="text-sm space-y-1">
                <div className="flex justify-between gap-4">
                  <span className="text-gray-500">Total Distance:</span>
                  <span className="font-bold text-indigo-600">{stats.total_kilometers.toFixed(2)} km</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-500">Points:</span>
                  <span className="font-bold">{stats.total_records}</span>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default StaffGPSTrackingPage;
